import { useContext, useEffect, useRef, useState } from "react";
import AppContext from "../../contexts/AppContext";
import FullScreenOverlay from "./overlay/FullScreenOverlay";
import styled from "styled-components";
import LayoutContext from "../../contexts/LayoutContext";

/* #region  SCOPED CSS */
const StyledPage = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: auto;
    padding: 24px;
    background: rgba(20, 25, 34, 0.95);
    gap: 24px;
    .video-preview,
    .photo-preview {
        border-radius: 12px;
        box-shadow: 0 2px 16px rgba(0, 0, 0, 0.3);
        max-width: 90vw;
        max-height: 60vh;
        background: #000;
    }
    .controls {
        display: flex;
        gap: 16px;
        margin-top: 16px;
    }
    button {
        padding: 10px 24px;
        border-radius: 8px;
        border: none;
        background: #5d3fd3;
        color: #fff;
        font-size: 16px;
        cursor: pointer;
        font-weight: 500;
    }
`;
/* #endregion */

export default function TakePhoto(props) {
    /* #region VARS */
    const { previewPhoto, previewPhotoBlob, onPhotoSelected } = props;

    // Contexts
    const { setFileUpload } = useContext(AppContext);
    const { setShowTakePhotoPopup } = useContext(LayoutContext);
    // Refs
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    // States
    const [photo, setPhoto] = useState(null); // base64 preview
    const [photoBlob, setPhotoBlob] = useState(null);
    const [error, setError] = useState("");
    /* #endregion */

    /* #region EFFECTS */
    useEffect(() => {
        if (!previewPhoto) startCamera();
        else {
            // If previewPhoto is provided, use it as the photo
            setPhoto(previewPhoto);
            setPhotoBlob(previewPhotoBlob);
        }

        return () => {
            stopCamera();
        };
    }, []); // Handles camera access on mount

    useEffect(() => {
        const handleKeyDown = event => {
            if (event.key === "Escape") {
                setShowTakePhotoPopup(false);
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []); // Handles Escape key to close the popup

    useEffect(() => {
        const handleClickOutside = event => {
            if (event.target.closest(".take-photo-popup")) return;
            setShowTakePhotoPopup(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []); // Handles click outside to close the popup
    /* #endregion */

    /* #region METHODS */
    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
            }
        } catch (e) {
            setError("Unable to access camera.");
        }
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    const handleTakePhoto = async () => {
        if (!videoRef.current) return;
        const video = videoRef.current;
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(async blob => {
            if (!blob) return;
            setPhotoBlob(blob);
            // Convert to base64 for preview
            const reader = new FileReader();
            reader.onloadend = () => setPhoto(reader.result);
            reader.readAsDataURL(blob);
        }, "image/png");
    };

    const handleUsePhoto = () => {
        if (!photoBlob || !photo) return;
        setFileUpload({
            file: photo,
            fileName: "camera-photo.png"
            //preview: photo
        });

        setShowTakePhotoPopup(false);

        if (onPhotoSelected) onPhotoSelected(photo, photoBlob);
    };

    const handleRetake = () => {
        setPhoto(null);
        setPhotoBlob(null);

        startCamera();
    };
    /* #endregion */

    /* #region RENDER */
    return (
        <FullScreenOverlay>
            <StyledPage className="take-photo-popup">
                {error && <div style={{ color: "red" }}>{error}</div>}
                {!photo ? (
                    <VideoPreview videoRef={videoRef} handleTakePhoto={handleTakePhoto} />
                ) : (
                    <PhotoPreview photo={photo} handleUsePhoto={handleUsePhoto} handleRetake={handleRetake} />
                )}
            </StyledPage>
        </FullScreenOverlay>
    );
    /* #endregion */
}

/* #region SMALL COMPONENTS */
const VideoPreview = ({ videoRef, handleTakePhoto }) => (
    <>
        <video className="video-preview" ref={videoRef} autoPlay playsInline style={{ width: "50vw", height: "calc(50vw * 0.75)" }} />
        <div className="controls">
            <button onClick={handleTakePhoto}>Take Photo</button>
        </div>
    </>
);

const PhotoPreview = ({ photo, handleUsePhoto, handleRetake }) => (
    <>
        <img
            className="photo-preview"
            src={photo}
            alt="Preview"
            style={{ width: "50vw", height: "calc(50vw * 0.75)", objectFit: "contain" }}
        />
        <div className="controls">
            <button onClick={handleUsePhoto}>Use Photo</button>
            <button onClick={handleRetake}>Retake</button>
        </div>
    </>
);
/* #endregion */
